import {
  Control,
  FabricObject,
  controlsUtils,
  TPointerEvent,
  Transform,
} from 'fabric';
import { useCanvasStateManager } from '@/stores';
import { getThemeColorVar } from '@/lib/theme';

/**
 * Custom Fabric.js Controls
 *
 * Control layout "corners" (default, pointer devices):
 * - Rotate (top-center)
 * - Delete (top-left, red accent)
 * - Resize (bottom-right)
 *
 * Control layout "touch" (Direct Sale on a phone):
 * - Delete (above the object, centred)
 * - Rotate and Move (below the object, side by side)
 * - Scaling from all four corner dots, no side handles
 * A thumb covers whatever it touches: handles at the object's own corners
 * hide the thing being moved, and a small text is nearly impossible to grab
 * by its body — hence the explicit move handle away from it.
 *
 * Border: dashed line in the theme selection color with round corner handles.
 */

// Colors matching the selection frame
const COLORS = {
  // Read live so it reflects an embedder-supplied theme (see src/lib/theme.ts)
  // instead of freezing whatever color was set at module-load time.
  get primary() {
    return getThemeColorVar('--color-canvas-selection', '#6366F1');
  },
  background: '#FFFFFF',
  danger: '#EF4444', // red — the only control that keeps its own accent
};

/** Diameter of the circular handles, in canvas pixels. */
export const CONTROL_SIZE = 32;
const CONTROL_OFFSET = 10; // Offset from corner, at CONTROL_SIZE
const CORNER_SIZE = 8; // Side handles (ml/mr/mt/mb), at CONTROL_SIZE
const LUCIDE_VIEWBOX = 24;

export interface CustomControlOptions {
  /**
   * Handle diameter in canvas pixels; defaults to CONTROL_SIZE.
   *
   * Fabric paints the handles onto the canvas, so a canvas that is displayed
   * scaled down (or up) by CSS shows them scaled the same way. A caller that
   * knows its display factor passes the size that lands at the wanted
   * on-screen diameter — the guided entry's small frame, for instance.
   */
  size?: number;
  /** Which arrangement to build; see the file header. Defaults to corners. */
  layout?: ControlLayout;
  /**
   * Selection border colour. The theme colour by default; an embedder that
   * knows what is behind the object passes one that stands out from it.
   */
  borderColor?: string;
}

export type ControlLayout = 'corners' | 'touch';

/** Touch layout: share of the hit area the visible handle takes up. */
const TOUCH_ICON_SCALE = 0.72;
/** Touch layout: rotation locks onto these steps (0/45/90/…/360°) … */
export const TOUCH_SNAP_ANGLE = 45;
/** … once the object is within this many degrees of one. */
export const TOUCH_SNAP_ANGLE_THRESHOLD = 6;

// Path data copied from lucide-react 0.468 (ISC): rotate-cw, trash-2, maximize-2.
// trash-2's `line` elements and maximize-2's `polyline`/`line` elements are
// expressed here as equivalent path `d` strings (Path2D has no line/polyline).
export const FRAME_ICON_PATHS = {
  rotate: ['M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8', 'M21 3v5h-5'],
  delete: [
    'M3 6h18',
    'M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6',
    'M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2',
    'M10 11L10 17',
    'M14 11L14 17',
  ],
  resize: ['M15 3L21 3L21 9', 'M9 21L3 21L3 15', 'M21 3L14 10', 'M3 21L10 14'],
  // lucide `move`, polylines expressed as paths.
  move: [
    'M12 2L12 22',
    'M2 12L22 12',
    'M9 5L12 2L15 5',
    'M9 19L12 22L15 19',
    'M5 9L2 12L5 15',
    'M19 9L22 12L19 15',
  ],
} as const satisfies Record<
  'rotate' | 'delete' | 'resize' | 'move',
  readonly string[]
>;

/**
 * Draw Lucide SVG paths centered in the current canvas context
 */
function drawLucidePaths(
  ctx: CanvasRenderingContext2D,
  paths: readonly string[],
  color: string,
  pixelSize: number
): void {
  const scale = pixelSize / LUCIDE_VIEWBOX;
  ctx.save();
  ctx.translate(-pixelSize / 2, -pixelSize / 2);
  ctx.scale(scale, scale);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (const d of paths) {
    ctx.stroke(new Path2D(d));
  }

  ctx.restore();
}

/**
 * Render a circular control with a Lucide icon
 */
function renderCircleControl(
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number,
  _styleOverride: object | undefined,
  _fabricObject: unknown,
  icon: keyof typeof FRAME_ICON_PATHS,
  size: number
) {
  ctx.save();
  ctx.translate(left, top);

  // Delete is the only control that keeps the red accent; rotate and resize
  // use the theme selection color for both the ring and the icon.
  const iconColor = icon === 'delete' ? COLORS.danger : COLORS.primary;
  const borderColor = iconColor;

  // Draw shadow
  ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 2;

  // Draw circle background
  ctx.beginPath();
  ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
  ctx.fillStyle = COLORS.background;
  ctx.fill();

  // Reset shadow for border
  ctx.shadowColor = 'transparent';
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Draw icon
  drawLucidePaths(ctx, FRAME_ICON_PATHS[icon], iconColor, size * 0.5);

  ctx.restore();
}

/**
 * Delete action handler.
 *
 * Exported as `deleteObjectForTest` only for unit testing; production code
 * should keep using it through `createCustomControls().deleteControl`.
 */
function deleteObject(
  _eventData: TPointerEvent,
  transform: Transform
): boolean {
  const target = transform.target;
  const canvas = target.canvas;
  if (!canvas) return false;
  // canvas.remove() ignores the ActiveSelection wrapper (it is never in
  // canvas._objects); remove the members like useObjectOperations.deleteSelected.
  // Duck-typed on purpose: works for real ActiveSelections and for test doubles.
  const members =
    'getObjects' in target &&
    typeof (target as { getObjects?: unknown }).getObjects === 'function'
      ? (target as unknown as { getObjects: () => FabricObject[] }).getObjects()
      : [target];
  for (const member of members) canvas.remove(member);
  canvas.discardActiveObject();
  canvas.requestRenderAll();
  // Toolbar and keyboard deletes record history; the frame icon must too.
  // object:removed already marks the position as user-cleared when the last
  // object goes (CanvasEditor) — do not duplicate that here.
  useCanvasStateManager.getState().saveToHistory('delete');
  return true;
}
export { deleteObject as deleteObjectForTest };

/**
 * Create custom controls for Fabric.js objects
 */
export function createCustomControls(
  size: number = CONTROL_SIZE,
  layout: ControlLayout = 'corners'
): Record<string, Control> {
  // Offsets keep their proportion to the handle, so a smaller handle also
  // sits closer to the corner instead of floating at the old distance.
  const offset = (CONTROL_OFFSET * size) / CONTROL_SIZE;

  if (layout === 'touch') {
    // Drawn smaller than they are hit: `size` is the thumb-sized target,
    // the visible disc is what sits next to the customer's artwork.
    const drawn = size * TOUCH_ICON_SCALE;
    const circle =
      (icon: keyof typeof FRAME_ICON_PATHS): Control['render'] =>
      (ctx, left, top, styleOverride, fabricObject) =>
        renderCircleControl(
          ctx,
          left,
          top,
          styleOverride,
          fabricObject,
          icon,
          drawn
        );
    // Clear of the frame and of the corner dots.
    const away = offset + size * 0.5;
    const apart = size * 0.6;
    const sized = {
      sizeX: size,
      sizeY: size,
      touchSizeX: size,
      touchSizeY: size,
    };

    return {
      deleteControl: new Control({
        ...sized,
        x: 0,
        y: -0.5,
        offsetX: 0,
        offsetY: -away,
        cursorStyle: 'pointer',
        render: circle('delete'),
        mouseUpHandler: deleteObject,
      }),
      rotateControl: new Control({
        ...sized,
        x: 0,
        y: 0.5,
        offsetX: -apart,
        offsetY: away,
        cursorStyle: 'crosshair',
        render: circle('rotate'),
        actionHandler: controlsUtils.rotationWithSnapping,
        actionName: 'rotate',
      }),
      moveControl: new Control({
        ...sized,
        x: 0,
        y: 0.5,
        offsetX: apart,
        offsetY: away,
        cursorStyle: 'move',
        render: circle('move'),
        actionHandler: controlsUtils.dragHandler,
        actionName: 'drag',
      }),
    };
  }

  return {
    // Rotate control (top-center)
    rotateControl: new Control({
      x: 0,
      y: -0.5,
      offsetX: 0,
      offsetY: -offset - (6 * size) / CONTROL_SIZE,
      sizeX: size,
      sizeY: size,
      touchSizeX: size,
      touchSizeY: size,
      cursorStyle: 'crosshair',
      render: (ctx, left, top, styleOverride, fabricObject) => {
        renderCircleControl(
          ctx,
          left,
          top,
          styleOverride,
          fabricObject,
          'rotate',
          size
        );
      },
      actionHandler: controlsUtils.rotationWithSnapping,
      actionName: 'rotate',
    }),

    // Delete control (top-left)
    deleteControl: new Control({
      x: -0.5,
      y: -0.5,
      offsetX: -offset,
      offsetY: -offset,
      sizeX: size,
      sizeY: size,
      touchSizeX: size,
      touchSizeY: size,
      cursorStyle: 'pointer',
      render: (ctx, left, top, styleOverride, fabricObject) => {
        renderCircleControl(
          ctx,
          left,
          top,
          styleOverride,
          fabricObject,
          'delete',
          size
        );
      },
      mouseUpHandler: deleteObject,
    }),

    // Resize control (bottom-right) - proportional scaling
    resizeControl: new Control({
      x: 0.5,
      y: 0.5,
      offsetX: offset,
      offsetY: offset,
      sizeX: size,
      sizeY: size,
      touchSizeX: size,
      touchSizeY: size,
      cursorStyle: 'se-resize',
      render: (ctx, left, top, styleOverride, fabricObject) => {
        renderCircleControl(
          ctx,
          left,
          top,
          styleOverride,
          fabricObject,
          'resize',
          size
        );
      },
      actionHandler: controlsUtils.scalingEqually,
      actionName: 'scale',
    }),
  };
}

/**
 * Apply custom controls to a Fabric.js object.
 *
 * Never touches `FabricObject.prototype.controls` — each instance gets its
 * own `controls` object (Fabric 6.9.1 `createControls`), so this must not
 * mutate shared state.
 */
export function applyCustomControls(
  object: FabricObject,
  options: CustomControlOptions = {}
): void {
  const size = options.size ?? CONTROL_SIZE;
  const layout = options.layout ?? 'corners';
  const customControls = createCustomControls(size, layout);

  // Get default controls from prototype if object doesn't have them
  const defaultControls = FabricObject.prototype.controls || {};
  const existingControls = object.controls || {};

  // Build controls object, only including defined controls
  const newControls: Record<string, Control> = {
    // Add custom controls first
    ...customControls,
  };

  // Keep middle side controls for non-uniform scaling if they exist
  // Check both existing controls and default controls
  // Touch: the four corner dots scale instead; side handles would invite
  // accidental stretching under a thumb.
  const keptControls =
    layout === 'touch'
      ? (['tl', 'tr', 'bl', 'br'] as const)
      : (['ml', 'mr', 'mt', 'mb'] as const);
  for (const key of keptControls) {
    const control = existingControls[key] || defaultControls[key];
    if (control) {
      newControls[key] = control;
    }
  }

  // Set the controls object
  object.controls = newControls;

  // Recalculate oCoords so findControl iterates the new control keys, not stale defaults
  if (object.canvas) {
    object.setCoords();
  }

  // Customize appearance - dashed border in the theme selection color.
  // Touch: a solid, heavier border. On a phone the selection has to read
  // at arm's length and against any garment colour, and it must stand out
  // from the (dashed) print area frame behind it.
  const borderColor = options.borderColor ?? COLORS.primary;
  object.set({
    borderColor,
    // IText draws its own border colour while it is being typed in.
    ...('editingBorderColor' in object
      ? { editingBorderColor: borderColor }
      : {}),
    borderDashArray: layout === 'touch' ? null : [5, 4],
    cornerColor: COLORS.background,
    cornerStrokeColor: COLORS.primary,
    cornerStyle: 'circle', // Round corner handles
    cornerSize:
      layout === 'touch' ? size * 0.4 : (CORNER_SIZE * size) / CONTROL_SIZE,
    // Drawn small, hit large.
    ...(layout === 'touch' ? { touchCornerSize: size } : {}),
    transparentCorners: false,
    borderScaleFactor: layout === 'touch' ? 2.5 : 1.5,
    padding: layout === 'touch' ? 4 : 1,
    borderOpacityWhenMoving: layout === 'touch' ? 1 : 0.8,
    // Fabric's rotationWithSnapping reads these; without them it never snaps.
    ...(layout === 'touch'
      ? {
          snapAngle: TOUCH_SNAP_ANGLE,
          snapThreshold: TOUCH_SNAP_ANGLE_THRESHOLD,
        }
      : {}),
  });

  // Hide default corner controls (we have custom ones)
  const corners = layout === 'touch';
  object.setControlsVisibility({
    // Corners: hidden, the custom resize handle scales. Touch: they scale.
    tl: corners,
    tr: corners,
    bl: corners,
    br: corners,
    mtr: false, // middle-top-rotate (we have custom)
  });
}
